import { EFlags } from '@feathr/blackbox';

/**
 * Groupings for the account Flags page. A flag belongs to exactly one category,
 * so the page can render every flag without duplicating or dropping one.
 *
 * Categories describe the part of the product a flag changes, because that is
 * how someone arrives at this page: they want to unlock Forms for an account,
 * or lift a budget limit, not find a flag whose name they already know.
 */
export enum EFlagCategory {
  Products = 'products',
  Integrations = 'integrations',
  Campaigns = 'campaigns',
  Audiences = 'audiences',
  Email = 'email',
  Reporting = 'reporting',
  Billing = 'billing',
  Interface = 'interface',
  Onboarding = 'onboarding',
  Access = 'access',
}

export interface IFlagCategoryMetadata {
  label: string;
  description: string;
}

/**
 * Render order for the Flags page. Declaration order is the display order, so
 * the categories most often reached for sit at the top.
 */
export const FLAG_CATEGORY_ORDER: EFlagCategory[] = [
  EFlagCategory.Products,
  EFlagCategory.Integrations,
  EFlagCategory.Campaigns,
  EFlagCategory.Audiences,
  EFlagCategory.Email,
  EFlagCategory.Reporting,
  EFlagCategory.Billing,
  EFlagCategory.Interface,
  EFlagCategory.Onboarding,
  EFlagCategory.Access,
];

export function flagCategoryMetadata(category: EFlagCategory): IFlagCategoryMetadata {
  const metadata: Record<EFlagCategory, IFlagCategoryMetadata> = {
    [EFlagCategory.Products]: {
      label: 'Products & modules',
      description: 'Turn whole product areas on or off for this account.',
    },
    [EFlagCategory.Integrations]: {
      label: 'Integrations',
      description: 'Third-party connections available on the integrations page.',
    },
    [EFlagCategory.Campaigns]: {
      label: 'Campaigns & ads',
      description: 'Campaign setup options, targeting, and budget enforcement.',
    },
    [EFlagCategory.Audiences]: {
      label: 'Audiences & data',
      description: 'Group building, contact limits, and conversion tracking.',
    },
    [EFlagCategory.Email]: {
      label: 'Email',
      description: 'Sending infrastructure, email limits, and activity exports.',
    },
    [EFlagCategory.Reporting]: {
      label: 'Reporting & goals',
      description: 'Report surfaces, goals, and conversion settings.',
    },
    [EFlagCategory.Billing]: {
      label: 'Billing & licensing',
      description: 'License tier overrides, usage metrics, and payment disclosures.',
    },
    [EFlagCategory.Interface]: {
      label: 'Interface & navigation',
      description: 'Navigation layout, labelling, and in-app conveniences.',
    },
    [EFlagCategory.Onboarding]: {
      label: 'Onboarding',
      description: 'The guided setup flow for new accounts.',
    },
    [EFlagCategory.Access]: {
      label: 'Admin & access',
      description: 'User management and API key administration.',
    },
  };
  return metadata[category];
}

export interface IFlagMetadata {
  label: string;
  description: string;
  category: EFlagCategory;
}

export function flagMetadata(flag: EFlags): IFlagMetadata {
  const metadata: Record<EFlags, IFlagMetadata> = {
    [EFlags.aiGroupBuilder]: {
      label: 'Show AI Group Builder',
      description: 'Reveals the AI group builder in the segment filter editor.',
      category: EFlagCategory.Audiences,
    },
    [EFlags.bypassMX]: {
      label: 'Bypass MX',
      description: 'Skips MX record verification when sending email from custom domains.',
      category: EFlagCategory.Email,
    },
    [EFlags.consentBannerEnabled]: {
      label: 'Landing Page Consent Banner',
      description:
        'Shows a cookie-consent banner on published landing pages. Display only; ' +
        'enforcement is server-side.',
      category: EFlagCategory.Interface,
    },
    [EFlags.creditCardConvenienceFeeAccepted]: {
      label: 'Credit Card Convenience Fee Accepted',
      description:
        'Suppresses the credit card surcharge disclosure once the account has acknowledged it.',
      category: EFlagCategory.Billing,
    },
    [EFlags.csmManager]: {
      label: 'CSM Manager',
      description: 'Renames CSM labels to "Account Manager" across the UI.',
      category: EFlagCategory.Interface,
    },
    [EFlags.enforceOnboarding]: {
      label: 'Enforce Onboarding',
      description: 'Blocks access to the main app until onboarding is completed.',
      category: EFlagCategory.Onboarding,
    },
    [EFlags.hasBlackbaudRaisersEdge]: {
      label: 'Has Blackbaud Raisers Edge',
      description: "Enables the Blackbaud Raiser's Edge NXT integration on the integrations page.",
      category: EFlagCategory.Integrations,
    },
    [EFlags.hasGoogleAds]: {
      label: 'Has Google Ads',
      description: 'Enables the Google Ads integration on the integrations page.',
      category: EFlagCategory.Integrations,
    },
    [EFlags.hasImis]: {
      label: 'Has IMIS',
      description: 'Enables the iMIS CRM integration on the integrations page.',
      category: EFlagCategory.Integrations,
    },
    [EFlags.hasMultiConversions]: {
      label: 'Has Multi Conversions',
      description: 'Allows campaigns to track multiple conversion events instead of a single one.',
      category: EFlagCategory.Audiences,
    },
    [EFlags.locales]: {
      label: 'Locales',
      description: 'Reveals the language / locale selector in the user profile.',
      category: EFlagCategory.Interface,
    },
    [EFlags.mailrail]: {
      label: 'Mailrail',
      description: 'Use Mailrail for email campaigns',
      category: EFlagCategory.Products,
    },
    [EFlags.metaIntegrationOutage]: {
      label: 'Meta Integration Outage',
      description: 'Disables the Meta integration UI to indicate an active service outage.',
      category: EFlagCategory.Integrations,
    },
    [EFlags.minBudget]: {
      label: 'Min Budget',
      description: 'Enforces the account-level minimum campaign budget.',
      category: EFlagCategory.Campaigns,
    },
    [EFlags.monetization]: {
      label: 'Monetization',
      description: 'Enables revenue and ROI tracking on budget cards and reports.',
      category: EFlagCategory.Products,
    },
    [EFlags.noMaxBudget]: {
      label: 'No Max Budget',
      description: 'Removes the upper cap on campaign budgets.',
      category: EFlagCategory.Campaigns,
    },
    [EFlags.noMinAudience]: {
      label: 'No Min Audience',
      description:
        'Removes the 2,000-person minimum-audience requirement (reachable audience and unique-email floor) when building campaigns.',
      category: EFlagCategory.Campaigns,
    },
    [EFlags.overridePackageImplementationsAccess]: {
      label: 'Override Implementations Access',
      description: "Grants implementation access regardless of the account's license tier.",
      category: EFlagCategory.Billing,
    },
    [EFlags.pinpointSendAllEmails]: {
      label: 'Pinpoint Send All Emails',
      description:
        'Adds the "send to all emails" option when configuring Pinpoint email audiences.',
      category: EFlagCategory.Email,
    },
    [EFlags.pinpoint]: {
      label: 'Pinpoint',
      description: 'Routes outgoing email through Amazon Pinpoint instead of the default sender.',
      category: EFlagCategory.Email,
    },
    [EFlags.showAccountReports]: {
      label: 'Show Account Reports',
      description: 'Adds the account-level (cross-campaign) reports section to the nav.',
      category: EFlagCategory.Reporting,
    },
    [EFlags.showAllAffinityAudiences]: {
      label: 'Show All Affinity Audiences',
      description: 'Removes category restrictions on affinity audiences in the ad wizard.',
      category: EFlagCategory.Campaigns,
    },
    [EFlags.showAPIKeysV2]: {
      label: 'Show API Keys V2',
      description: 'Switches the API keys settings page to the v2 scoped service-key UI.',
      category: EFlagCategory.Access,
    },
    [EFlags.showCampaignRecommendations]: {
      label: 'Show Campaign Recommendations',
      description: 'Enables optimization recommendations on TTD ad campaigns.',
      category: EFlagCategory.Campaigns,
    },
    [EFlags.showCommandPalette]: {
      label: 'Show Command Palette',
      description: 'Enables the ⌘K command palette for quick navigation and search.',
      category: EFlagCategory.Interface,
    },
    [EFlags.showConversionsSettingsV2]: {
      label: 'Show Conversions Settings V2',
      description: 'Switches the conversions settings page to the v2 attribution-window UI.',
      category: EFlagCategory.Reporting,
    },
    [EFlags.showDripBulkEnroll]: {
      label: 'Show Drip Bulk Enroll',
      description: 'Enables bulk enrollment of contacts into drip campaigns.',
      category: EFlagCategory.Products,
    },
    [EFlags.showDripCampaigns]: {
      label: 'Show Drip Campaigns',
      description: 'Enables drip / automation campaign creation and management.',
      category: EFlagCategory.Products,
    },
    [EFlags.showEmailActivityExport]: {
      label: 'Show Email Activity Export',
      description: 'Adds the export option to email activity reports.',
      category: EFlagCategory.Email,
    },
    [EFlags.showForms]: {
      label: 'Show Forms',
      description: 'Enables the Forms feature (form creation, embedding, and management).',
      category: EFlagCategory.Products,
    },
    [EFlags.showFundraiseCRM]: {
      label: 'Show Fundraising CRM',
      description: 'Enables CRM integration settings within Fundraising campaigns.',
      category: EFlagCategory.Products,
    },
    [EFlags.showFundraise]: {
      label: 'Show Fundraising',
      description: 'Enables Fundraising campaign features in the nav and main UI.',
      category: EFlagCategory.Products,
    },
    [EFlags.showGoalsV2]: {
      label: 'Show Goals 2.0',
      description: 'Switches goal creation and management to the v2 UI.',
      category: EFlagCategory.Reporting,
    },
    [EFlags.showGrowthCredits]: {
      label: 'Show Growth Credits',
      description: 'Shows the growth credits card at the bottom of the primary nav.',
      category: EFlagCategory.Billing,
    },
    [EFlags.showNewPrimaryNav]: {
      label: 'Show New Primary Nav',
      description: 'Switches to the redesigned primary navigation layout.',
      category: EFlagCategory.Interface,
    },
    [EFlags.showOnboarding]: {
      label: 'Show Onboarding',
      description:
        'Reveals the onboarding flow in the nav (pair with Enforce Onboarding to require it).',
      category: EFlagCategory.Onboarding,
    },
    [EFlags.showProResources]: {
      label: 'Show Pro Resources',
      description:
        'Reveals the Pro Resources page and unlocks implementation access for non-premium packages.',
      category: EFlagCategory.Billing,
    },
    [EFlags.showUsageMetrics]: {
      label: 'Show Usage Metrics',
      description: 'Shows billing usage metrics on the License page.',
      category: EFlagCategory.Billing,
    },
    [EFlags.showUsersAndRolesV2]: {
      label: 'Show Users and Roles V2',
      description: 'Switches users and roles management to the v2 UI.',
      category: EFlagCategory.Access,
    },
    [EFlags.useContactLimits]: {
      label: 'Use Contact Limits',
      description: 'Enforces contact / audience size limits on segments and campaigns.',
      category: EFlagCategory.Audiences,
    },
    [EFlags.useEmailLimits]: {
      label: 'Use Email Limits',
      description: 'Enforces email sending limits and shows the monthly / per-campaign breakdown.',
      category: EFlagCategory.Email,
    },
    [EFlags.zapier]: {
      label: 'Zapier',
      description: 'Enables the Zapier integration card on the integrations page.',
      category: EFlagCategory.Integrations,
    },
  };
  return metadata[flag];
}
